import { createContext, useContext } from 'react';

/**
 * Portal Platform Data Context
 *
 * This context provides access to portal platform data including brand settings,
 * theme settings, and other portal configuration. The data comes from
 * initializePortal which fetches brand and theme data and sets up the portal theme.
 */ // Type definition for the Portal Platform data structure from initializePortal
// Re-export types from portals-platform-lib for convenience
// Logo type (matches LogoValue from portals-platform-lib)
// Create the Portal Platform Data context
export const PortalPlatformDataContext = /*#__PURE__*/createContext(null);

/**
 * Hook to access the complete Portal Platform data
 * @returns The portal platform data including brand settings, theme settings, etc.
 */
export const usePortalPlatformData = () => {
  const context = useContext(PortalPlatformDataContext);
  if (!context) {
    throw new Error('usePortalPlatformData must be used within a PortalPlatformDataContext.Provider');
  }
  return context;
};

/**
 * Hook to access brand settings from the portal platform data
 * @returns Brand settings including logo, colors, fonts, etc.
 */
export const useBrandSettings = () => {
  var _portalData$context;
  const portalData = usePortalPlatformData();
  return (portalData === null || portalData === void 0 || (_portalData$context = portalData.context) === null || _portalData$context === void 0 ? void 0 : _portalData$context.brandSettings) || null;
};

/**
 * Hook to access theme settings from the portal platform data
 * @returns Theme settings from the portal configuration
 */
export const useThemeSettings = () => {
  var _portalData$context2;
  const portalData = usePortalPlatformData();
  return (portalData === null || portalData === void 0 || (_portalData$context2 = portalData.context) === null || _portalData$context2 === void 0 ? void 0 : _portalData$context2.themeSettings) || null;
};

/**
 * Hook to access the brand logo from brand settings
 * @returns Brand logo information or null if not available
 */
export const useBrandLogo = () => {
  const brandSettings = useBrandSettings();
  return (brandSettings === null || brandSettings === void 0 ? void 0 : brandSettings.logo) || null;
};

/**
 * Hook to access the company name from the portal platform data
 * @returns Company name or null if not available
 */
export const useCompanyName = () => {
  var _portalData$context3;
  const portalData = usePortalPlatformData();
  return (portalData === null || portalData === void 0 || (_portalData$context3 = portalData.context) === null || _portalData$context3 === void 0 ? void 0 : _portalData$context3.companyName) || null;
};

/**
 * Hook to access the brand name from the portal platform data
 * @returns Brand name or null if not available
 */
export const useBrandName = () => {
  var _portalData$context4;
  const portalData = usePortalPlatformData();
  return (portalData === null || portalData === void 0 || (_portalData$context4 = portalData.context) === null || _portalData$context4 === void 0 ? void 0 : _portalData$context4.brandName) || null;
};